import React, { useEffect, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { DocumentViewer, LoadingSpinner } from '../components'
import { Card } from '../components/ui'
import { API_BASE_URL, DocumentViewerMeta, LoadingSpinnerMeta } from '../data'
import { Guideline, GuidelineDetailsMetaProps } from '../data/types'
import { ChevronLeftIcon, DocumentIcon } from '../icons'

type Props = {
    meta: GuidelineDetailsMetaProps
}

export const GuidelineDetails = ({ meta }: Props) => {
    const { guidelineId } = useParams()
    const location = useLocation() as { state?: { guideline?: Guideline } }

    const [guideline, setGuideline] = useState<Guideline | null>(location.state?.guideline ?? null)
    const [loading, setLoading] = useState(!location.state?.guideline)
    const [error, setError] = useState<string | null>(null)

    const [selectedModuleIndex, setSelectedModuleIndex] = useState(0)
    const [documentUrl, setDocumentUrl] = useState<string | null>(null)
    const [documentLoading, setDocumentLoading] = useState(false)
    const [documentError, setDocumentError] = useState<string | null>(null)

    // Fallback fetch — covers refresh / direct link, since router state doesn't survive a reload
    useEffect(() => {
        if (guideline) return

        const fetchGuideline = async () => {
            setLoading(true)
            setError(null)
            try {
                const response = await fetch(`${API_BASE_URL}/volunteer/guideline`, { credentials: 'include' })
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`)
                }
                const list: Guideline[] = await response.json()
                const found = list.find(g => g.id === Number(guidelineId))
                if (!found) {
                    throw new Error(meta.notFoundMessage)
                }
                setGuideline(found)
            } catch (err) {
                setError(err instanceof Error ? err.message : meta.notFoundMessage)
            } finally {
                setLoading(false)
            }
        }

        fetchGuideline()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guidelineId])

    // Resolve the selected module's file_key into a viewable URL (same pattern as TrainingModule.tsx)
    useEffect(() => {
        if (!guideline) return
        const module = guideline.content?.modules?.[selectedModuleIndex]
        if (!module) return

        const resolveDocument = async () => {
            setDocumentLoading(true)
            setDocumentError(null)
            setDocumentUrl(null)
            try {
                const response = await fetch(`${API_BASE_URL}/file/${encodeURIComponent(module.file_key)}`, { credentials: 'include' })
                if (!response.ok) {
                    throw new Error('Failed to load document')
                }
                const result = await response.json()
                if (!result.success || !result.data?.url) {
                    throw new Error('Document source not found')
                }
                setDocumentUrl(result.data.url + '#toolbar=0&navpanes=0')
            } catch (err) {
                setDocumentError(err instanceof Error ? err.message : 'Failed to load document')
            } finally {
                setDocumentLoading(false)
            }
        }

        resolveDocument()
    }, [guideline, selectedModuleIndex])

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-[400px]">
                <LoadingSpinner meta={LoadingSpinnerMeta} />
            </div>
        )
    }

    if (error || !guideline) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-6 py-4 rounded-lg max-w-2xl mx-auto">
                    <p className="font-bold text-lg mb-2">{meta.errorTitle}</p>
                    <p>{error ?? meta.notFoundMessage}</p>
                </div>
            </div>
        )
    }

    const modules = guideline.content?.modules ?? []

    return (
        <div className="flex-1 container mx-auto px-4 py-8">
            <div className="mb-6">
                <Link to="/volunteer/guidelines" className="inline-flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300">
                    <ChevronLeftIcon className="w-5 h-5 me-2" />
                    {meta.backButtonLabel}
                </Link>
            </div>

            <Card className="mb-6">
                <div className="p-6">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">{guideline.name}</h1>
                    {guideline.description && (
                        <p className="text-gray-600 dark:text-gray-400">{guideline.description}</p>
                    )}
                </div>
            </Card>

            {modules.length > 1 && (
                <div className="flex gap-2 mb-4 overflow-x-auto">
                    {modules.map((module, index) => (
                        <button
                            key={index}
                            onClick={() => setSelectedModuleIndex(index)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${index === selectedModuleIndex
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                        >
                            <DocumentIcon className="w-4 h-4" />
                            {module.title}
                        </button>
                    ))}
                </div>
            )}

            {modules.length === 0 && (
                <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                    <p>{meta.notFoundMessage}</p>
                </div>
            )}

            {documentLoading && (
                <div className="flex justify-center items-center min-h-[300px]">
                    <LoadingSpinner meta={LoadingSpinnerMeta} />
                </div>
            )}

            {documentError && (
                <div className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-6 py-4 rounded-lg">
                    <p>{documentError}</p>
                </div>
            )}

            {!documentLoading && !documentError && documentUrl && modules[selectedModuleIndex] && (
                <DocumentViewer
                    fileUrl={documentUrl}
                    title={modules[selectedModuleIndex].title}
                    meta={DocumentViewerMeta}
                    isGuideline={true}
                />
            )}
        </div>
    )
}
